import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { checkNotNil } from "../../check-not-nil";
import {
  type FormSubmissionHandler,
  formSubmissionHandler,
} from "../../form.js";
import { ValidationError } from "../../schemas/validation-error";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";

export class WmMetricCreate extends WmElement {
  static readonly TAG_NAME = "wm-metric-create";

  static styles = [
    hostStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button {
        align-self: start;
      }
    `,
  ];

  #formSubmit: FormSubmissionHandler;

  constructor() {
    super();
    this.#formSubmit = formSubmissionHandler("/app/metrics", {
      method: "POST",
    })
      .on("pre:response", () => (this.#querySubmitButton().loading = true))
      .on("response", (r) => this.#handleResponse(r))
      .on("post:response", () => (this.#querySubmitButton().loading = false))
      .on("error", (err) =>
        this.logger.error("unable to POST /app/metrics", err),
      );
  }

  protected override render() {
    // TODO: options must be loaded from the server.
    // TODO: units must be loaded from the server, when applicable.
    return html`
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wa-input
          label="Name"
          name="name"
          required
          @input=${this.#nameInput}>
        </wa-input>
        <wm-metric-definition>
        </wm-metric-definition>
        <wm-tags-combobox>
        </wm-tags-combobox>
        <wa-button variant="success" type="submit">Create</wa-button>
      </form>
    `;
  }

  #nameInput(e: InputEvent) {
    e.stopPropagation();
    const input = checkNotNil(e.target) as WaInput;
    // Reset the custom validity, if any was set before.
    input.setCustomValidity("");
  }

  async #handleResponse(response: Response) {
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      if (path === "name") {
        const input = this.#queryNameInput();
        input.setCustomValidity(reason);
        input.reportValidity();
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          path,
          validationError,
        );
      }
    } else {
      return htmx.ajax("GET", "/app/metrics", { push: "true" });
    }
  }

  #queryNameInput(): WaInput {
    return this.queryNotNil("wa-input");
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmMetricCreate.TAG_NAME, WmMetricCreate);

declare global {
  interface HTMLElementTagNameMap {
    [WmMetricCreate.TAG_NAME]: WmMetricCreate;
  }
}
